import { useState } from 'react'
import { useQueryClient } from 'react-query'

import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'

import { DocumentDoesNotExistError } from 'utils/error/DocumentDoesNotExistError'
import { createProgramRoom, fetchRoomByProgramId, fetchUpspaceRoom } from 'utils/queries'
import { Toast } from 'utils/toast'

import { Room } from 'types/chat'
import { Program } from 'types/program'

export const useUpspaceRoom = ({ program, userId }: { program?: Program; userId?: string }) => {
  const queryKey = ['programRoom', program?.id]

  const queryClient = useQueryClient()

  const [roomId, setRoomId] = useState<string | undefined>(
    queryClient.getQueryData<Room>(queryKey)?.id || undefined,
  )

  const roomQuery = useQuery<Room | null>(
    ['room', roomId],
    () => (roomId && userId ? fetchUpspaceRoom({ roomId, userId }) : Promise.resolve(null)),
    {
      enabled: !!roomId && !!userId,
    },
  )

  const createRoom = useMutation<{ id: string }>(
    () => (program ? createProgramRoom({ programId: program?.id }) : Promise.reject()),
    {
      onSuccess: (data) => {
        setRoomId(data.id)
      },
      onError: () => {
        Toast.error('Unable to create program chat room', 'Please try again')
      },
    },
  )

  const programRoomQuery = useQuery<Room | null>(
    queryKey,
    () => (program ? fetchRoomByProgramId({ programId: program.id }) : Promise.resolve(null)),
    {
      enabled: !!program,
      onSuccess: (data) => {
        if (data?.id) {
          setRoomId(data.id)
        }
      },
      onError: (error) => {
        if ((error as { type?: string })?.type === DocumentDoesNotExistError.type) {
          createRoom.mutate()
        } else {
          Toast.error('Unable to find program chat room', 'Please try again')
        }
      },
    },
  )

  return {
    ...roomQuery,
    programRoomQuery,

    // Mutations
    createRoom,
  }
}
